import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  type ProfessorScope,
  isProfessorScope,
  professorScopeStorageKey,
} from '@/types/professorScope';

interface ProfessorScopeContextValue {
  professorScope: ProfessorScope;
  setProfessorScope: (scope: ProfessorScope) => void;
}

const ProfessorScopeContext = createContext<ProfessorScopeContextValue>({
  professorScope: { type: 'all' },
  setProfessorScope: () => {},
});

/**
 * Provider do escopo "ver como" do modo professor (ver types/professorScope.ts).
 * Precisa estar DENTRO de `<AuthProvider>` (consome `useAuth()` para `user`).
 * Deliberadamente separado de `AuthContext`/`viewScope`: escopo por professor
 * so existe dentro de um UNICO clube (sem troca de clube envolvida), entao
 * nao precisa do state machine de validacao contra rede que `viewScope` tem
 * em AuthBridge.tsx — mante-lo isolado evita tocar naquele arquivo, ja
 * complexo, para uma feature de escopo conceitualmente diferente.
 */
export function ProfessorScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [professorScope, setProfessorScopeState] = useState<ProfessorScope>({ type: 'all' });

  useEffect(() => {
    if (!user) {
      setProfessorScopeState({ type: 'all' });
      return;
    }
    try {
      const raw = window.localStorage.getItem(professorScopeStorageKey(user.id));
      const parsed = raw ? JSON.parse(raw) : null;
      setProfessorScopeState(isProfessorScope(parsed) ? parsed : { type: 'all' });
    } catch {
      setProfessorScopeState({ type: 'all' });
    }
  }, [user]);

  const setProfessorScope = (scope: ProfessorScope): void => {
    setProfessorScopeState(scope);
    if (user) {
      try {
        window.localStorage.setItem(professorScopeStorageKey(user.id), JSON.stringify(scope));
      } catch {
        // localStorage indisponivel (modo privado etc.) — segue sem persistir.
      }
    }
  };

  return (
    <ProfessorScopeContext.Provider value={{ professorScope, setProfessorScope }}>
      {children}
    </ProfessorScopeContext.Provider>
  );
}

export function useProfessorScope(): ProfessorScopeContextValue {
  return useContext(ProfessorScopeContext);
}
